import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Schedule,
  Refresh,
  Warning,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const SessionIndicator = () => {
  const { 
    user, 
    getSessionTimeRemaining, 
    extendSession, 
    logout,
    sessionExpiry 
  } = useAuth();
  
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    if (!user || !sessionExpiry) return;

    const updateTimer = () => {
      const remaining = getSessionTimeRemaining();
      setTimeRemaining(remaining);
      
      // Show warning when less than 10 minutes remain
      const tenMinutes = 10 * 60 * 1000;
      if (remaining <= tenMinutes && remaining > 0) {
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [user, sessionExpiry, getSessionTimeRemaining]);

  const formatTime = (milliseconds) => {
    const totalMinutes = Math.floor(milliseconds / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const handleExtendSession = () => {
    extendSession();
    setShowWarning(false);
  };

  const handleLogout = () => {
    logout();
    setShowWarning(false);
  };

  if (!user) return null;

  const isWarning = timeRemaining <= 10 * 60 * 1000; // 10 minutes
  const isCritical = timeRemaining <= 5 * 60 * 1000; // 5 minutes

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title="Session time remaining">
          <Chip
            icon={<Schedule />}
            label={formatTime(timeRemaining)}
            color={isCritical ? 'error' : isWarning ? 'warning' : 'default'}
            size="small"
            variant={isWarning ? 'filled' : 'outlined'}
          />
        </Tooltip>
        
        <Tooltip title="Extend session">
          <IconButton
            size="small"
            onClick={handleExtendSession}
            color={isWarning ? 'warning' : 'default'}
          >
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Session Warning Dialog */}
      <Dialog 
        open={showWarning}
        onClose={() => setShowWarning(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          Session Expiring Soon
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Your session will expire in <strong>{formatTime(timeRemaining)}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Would you like to extend your session or log out now?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogout} color="error">
            Logout Now
          </Button>
          <Button 
            onClick={handleExtendSession} 
            variant="contained" 
            color="primary"
            autoFocus
          >
            Extend Session
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SessionIndicator;